/* Celestial Projectors — CX interactions (vanilla, no deps) */
(() => {
  'use strict';
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));

  /* ---------------- Reveal on scroll ---------------- */
  function initReveal(root = document) {
    const els = $$('.cx-reveal:not(.is-in)', root);
    if (!('IntersectionObserver' in window) || reduce || document.hidden) { els.forEach(e => e.classList.add('is-in')); return; }
    const io = new IntersectionObserver((ents) => {
      ents.forEach(en => { if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    els.forEach(e => io.observe(e));
  }

  /* ---------------- Visibility helper (pause offscreen) ---------------- */
  function onVisible(el, cb) {
    if (!('IntersectionObserver' in window)) { cb(true); return; }
    new IntersectionObserver(([e]) => cb(e.isIntersecting), { threshold: 0.01 }).observe(el);
  }

  /* ---------------- Hero starfield (2D canvas, parallax) ---------------- */
  function initStarfield(canvas) {
    const ctx = canvas.getContext('2d');
    let w, h, dpr, stars = [], shooting = [], mx = 0, my = 0, tx = 0, ty = 0, running = false, raf;
    function resize() {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.clientWidth; h = canvas.clientHeight;
      canvas.width = w * dpr; canvas.height = h * dpr; ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const n = Math.round(Math.min(900, (w * h) / 1600));
      stars = Array.from({ length: n }, () => {
        const z = Math.random();
        const hue = Math.random() < 0.15 ? (Math.random() < 0.5 ? 265 : 200) : 0;
        return { x: Math.random() * w, y: Math.random() * h, z, r: 0.25 + z * z * 1.6, a: 0.25 + z * 0.75, tw: Math.random() * 6.28, sp: 0.4 + Math.random() * 1.6, hue };
      });
    }
    function frame(t) {
      if (!running) return;
      raf = requestAnimationFrame(frame);
      tx += (mx - tx) * 0.04; ty += (my - ty) * 0.04;
      ctx.clearRect(0, 0, w, h);
      for (const s of stars) {
        const px = s.x + tx * s.z * 26, py = s.y + ty * s.z * 18 + (window.scrollY || 0) * s.z * -0.08;
        const yy = ((py % h) + h) % h;
        const a = s.a * (0.65 + 0.35 * Math.sin(t * 0.001 * s.sp + s.tw));
        ctx.fillStyle = s.hue ? `hsla(${s.hue},90%,80%,${a})` : `rgba(255,255,255,${a})`;
        ctx.beginPath(); ctx.arc(px, yy, s.r, 0, 6.283); ctx.fill();
        if (s.r > 1.45) { ctx.fillStyle = `rgba(200,180,255,${a * 0.12})`; ctx.beginPath(); ctx.arc(px, yy, s.r * 4, 0, 6.283); ctx.fill(); }
      }
      if (!reduce && Math.random() < 0.006 && shooting.length < 2) shooting.push({ x: Math.random() * w * 0.8 + w * 0.2, y: Math.random() * h * 0.4, vx: -(6 + Math.random() * 5), vy: 2.4 + Math.random() * 2, life: 1 });
      shooting = shooting.filter(s => s.life > 0);
      for (const s of shooting) {
        const g = ctx.createLinearGradient(s.x, s.y, s.x - s.vx * 14, s.y - s.vy * 14);
        g.addColorStop(0, `rgba(255,255,255,${s.life})`); g.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.strokeStyle = g; ctx.lineWidth = 1.4; ctx.beginPath(); ctx.moveTo(s.x, s.y); ctx.lineTo(s.x - s.vx * 14, s.y - s.vy * 14); ctx.stroke();
        s.x += s.vx; s.y += s.vy; s.life -= 0.014;
      }
    }
    resize(); addEventListener('resize', resize, { passive: true });
    addEventListener('pointermove', e => { mx = (e.clientX / innerWidth - 0.5) * 2; my = (e.clientY / innerHeight - 0.5) * 2; }, { passive: true });
    onVisible(canvas, v => { if (v && !running) { running = true; raf = requestAnimationFrame(frame); } else if (!v) { running = false; cancelAnimationFrame(raf); } });
    if (reduce) { running = true; frame(0); running = false; }
  }

  /* ---------------- WebGL sky renderer (shared by Explorer + Visualizer) ---------------- */
  const VERT = 'attribute vec2 p;void main(){gl_Position=vec4(p,0.,1.);}';
  const FRAG = `
precision highp float;
uniform vec2 uRes; uniform float uTime; uniform int uMode; uniform int uEffect;
uniform float uHue; uniform float uInt; uniform float uAmb; uniform float uYaw;
uniform vec2 uProjS; uniform sampler2D uPhoto; uniform vec2 uPhotoAR;
uniform vec3 uBmin[8]; uniform vec3 uBmax[8]; uniform vec3 uBcol[8]; uniform float uBem[8]; uniform int uBn;
uniform vec3 uP; uniform vec3 uAxis; uniform vec3 uWall; uniform vec3 uFloor;
float h21(vec2 p){p=fract(p*vec2(123.34,456.21));p+=dot(p,p+45.32);return fract(p.x*p.y);}
vec2 h22(vec2 p){float n=h21(p);return vec2(n,h21(p+n));}
float noise(vec2 p){vec2 i=floor(p),f=fract(p);f=f*f*(3.-2.*f);
 return mix(mix(h21(i),h21(i+vec2(1,0)),f.x),mix(h21(i+vec2(0,1)),h21(i+vec2(1,1)),f.x),f.y);}
float fbm(vec2 p){float v=0.,a=.5;for(int i=0;i<5;i++){v+=a*noise(p);p=p*2.03+vec2(17.1,9.2);a*=.5;}return v;}
mat2 rot(float a){float c=cos(a),s=sin(a);return mat2(c,-s,s,c);}
vec3 hueShift(vec3 c,float a){const vec3 k=vec3(.57735);float ca=cos(a);return c*ca+cross(k,c)*sin(a)+k*dot(k,c)*(1.-ca);}
float starField(vec2 p,float scale,float prob,float t){vec2 g=p*scale;vec2 id=floor(g);vec2 f=fract(g)-.5;float r=h21(id);
 if(r>prob)return 0.;vec2 o=(h22(id+3.1)-.5)*.7;float d=length(f-o);float tw=.6+.4*sin(t*(1.+r*3.)+r*40.);
 return smoothstep(.09,.0,d)*tw*(.4+.6*fract(r*13.));}
vec3 fxGalaxy(vec2 st,float t){
 st*=rot(t*.025);float r=length(st);
 vec2 q=vec2(fbm(st*1.4+t*.02),fbm(st*1.4-vec2(5.2,1.3)-t*.015));
 float n=fbm(st*1.7+q*1.8);
 float n2=fbm(st*3.1-q*2.4+11.);
 vec3 c=mix(vec3(.10,.25,1.),vec3(.62,.18,1.),smoothstep(.3,.7,q.x));
 c=mix(c,vec3(1.,.28,.72),smoothstep(.55,.85,n2)*.8);
 float neb=pow(smoothstep(.28,.95,n),1.6)*1.7;
 vec3 col=c*neb*(1.15-r*.25);
 col+=vec3(1.)*starField(st,46.,.22,t)*1.2+vec3(.85,.9,1.)*starField(st+3.,95.,.18,t*1.3)*.8;
 return col;}
vec3 fxAurora(vec2 st,float t){
 vec3 col=vec3(0.);
 for(int i=0;i<3;i++){float fi=float(i);
  float y=st.y+.35-fi*.32+.22*sin(st.x*1.6+t*.25+fi*2.1)+.18*(fbm(vec2(st.x*1.2+t*.06,fi*4.))-.5);
  float band=exp(-abs(y)*7.)*(.55+.9*fbm(vec2(st.x*7.+fi*10.,t*.25)));
  float streak=.72+.28*noise(vec2(st.x*14.+t*.3,fi*3.));
  vec3 cc=fi<.5?vec3(.18,1.,.62):(fi<1.5?vec3(.18,.8,1.):vec3(.7,.3,1.));
  col+=cc*band*streak*1.25;}
 col+=vec3(1.)*starField(st,60.,.16,t)*.9;
 return col;}
float voro(vec2 p,float t){vec2 i=floor(p),f=fract(p);float d1=8.,d2=8.;
 for(int y=-1;y<=1;y++)for(int x=-1;x<=1;x++){vec2 g=vec2(float(x),float(y));vec2 o=h22(i+g);o=.5+.42*sin(t+6.2831*o);float d=length(g+o-f);
  if(d<d1){d2=d1;d1=d;}else if(d<d2){d2=d;}}
 return d2-d1;}
vec3 fxOcean(vec2 st,float t){
 vec2 w=st+.38*vec2(fbm(st*1.6+t*.08),fbm(st*1.6-t*.08+4.));
 float c1=1.-smoothstep(0.,.3,voro(w*2.6,t*.5));
 float c2=1.-smoothstep(0.,.26,voro(w*4.1+3.,t*.7));
 float c=pow(c1*c2,1.3)*1.5+pow(c1,3.)*.25;
 vec3 base=vec3(.01,.09,.32)*(.6+.6*fbm(st*1.5+t*.04));
 return base+vec3(.35,.85,1.)*c*1.05;}
vec3 fxLaser(vec2 st,float t){
 vec2 p=st*rot(t*.03);
 float g=starField(p,70.,.55,t*2.);float r=starField(p+7.7,70.,.25,t*2.3);
 vec3 col=vec3(.25,1.,.35)*g*2.6+vec3(1.,.18,.2)*r*2.2;
 col+=vec3(.05,.2,.9)*pow(fbm(p*1.5+t*.04),3.)*1.4;
 return col;}
vec3 fxPlanet(vec2 st,float t){
 vec2 p=st*rot(t*.01);
 float mw=exp(-pow(dot(p,normalize(vec2(1.,.55))*mat2(0,-1,1,0)),2.)*9.);
 float dust=fbm(p*4.+3.);float lanes=smoothstep(.45,.75,fbm(p*7.-2.));
 vec3 col=vec3(.55,.6,1.)*mw*pow(dust,1.6)*(1.-.6*lanes)*.55;
 col+=vec3(1.)*starField(p,22.,.18,t)*2.2+vec3(.9,.95,1.)*starField(p+1.,55.,.35,t)*1.4+vec3(.8,.85,1.)*starField(p+2.,120.,.45,t)*(.6+mw);
 col+=vec3(.04,.06,.2);
 return col;}
float sdStar(vec2 p,float r,float rf){const float an=3.141593/5.;float bn=mod(atan(p.x,p.y),2.*an)-an;p=length(p)*vec2(cos(bn),abs(sin(bn)));
 vec2 k1=vec2(cos(an),sin(an));p-=r*vec2(1,0);vec2 ba=rf*vec2(-k1.y,k1.x)-vec2(1,0);float h=clamp(dot(p,ba)/dot(ba,ba),0.,r);return length(p-ba*h)*sign(p.y*ba.x-p.x*ba.y);}
vec3 fxStarMoon(vec2 st,float t){
 vec2 p=st*rot(t*.08);float a=atan(p.y,p.x);float r=length(p);
 float seg=6.2831/10.;float ia=floor(a/seg);float la=mod(a,seg)-seg*.5;vec2 q=vec2(cos(la),sin(la))*r;
 vec3 col=vec3(0.);
 for(int k=0;k<3;k++){float fk=float(k);float rr=.35+fk*.38;vec2 c=q-vec2(rr,0.);
  float hsh=h21(vec2(ia,fk));float sz=.06+.025*fk;float an2=atan(c.y,c.x)+hsh*6.;
  float rs=sz*(.32+.68*pow(abs(cos(an2*2.5)),6.));float m=smoothstep(rs,rs-.012,length(c));
  float moonD=length(c)-.06-fk*.02;float moonC=length(c-vec2(.03+.01*fk,.02))-.055-fk*.02;float moon=smoothstep(.01,0.,max(moonD,-moonC));
  float pick=step(.78,hsh);
  vec3 cc=mix(vec3(1.,.82,.4),vec3(.55,.75,1.),fract(hsh*7.));
  col+=cc*mix(m,moon,pick)*1.5;}
 col+=vec3(.25,.15,.5)*.15;
 return col;}
vec3 effect(vec2 st,float t){
 vec3 c;
 if(uEffect==0)c=fxGalaxy(st,t);else if(uEffect==1)c=fxAurora(st,t);else if(uEffect==2)c=fxOcean(st,t);
 else if(uEffect==3)c=fxLaser(st,t);else if(uEffect==4)c=fxPlanet(st,t);else c=fxStarMoon(st,t);
 return max(hueShift(c,uHue),0.);}
vec2 boxT(vec3 ro,vec3 rd,vec3 a,vec3 b,out vec3 n){vec3 inv=1./rd;vec3 t0=(a-ro)*inv,t1=(b-ro)*inv;vec3 tn=min(t0,t1),tf=max(t0,t1);
 float tN=max(max(tn.x,tn.y),tn.z);float tF=min(min(tf.x,tf.y),tf.z);
 n=-sign(rd)*step(tn.yzx,tn.xyz)*step(tn.zxy,tn.xyz);return vec2(tN,tF);}
float shadow(vec3 x,vec3 l){vec3 d=l-x;float L=length(d);d/=L;vec3 n;
 for(int i=0;i<8;i++){if(i>=uBn)break;if(uBem[i]>.5)continue;vec2 t=boxT(x,d,uBmin[i],uBmax[i],n);if(t.x<t.y&&t.x>.001&&t.x<L)return 0.;}
 return 1.;}
vec3 tonemap(vec3 c){c=1.-exp(-c*1.25);return pow(c,vec3(1./2.2));}
void main(){
 vec2 uv=gl_FragCoord.xy/uRes;float t=uTime;float ar=uRes.x/uRes.y;
 if(uMode==1){ // explorer: full-frame sky
  vec2 st=(uv-vec2(.5,.62))*vec2(ar,1.)*1.9;
  vec3 c=effect(st,t)*uInt;
  float v=smoothstep(1.5,.2,length((uv-vec2(.5,.55))*vec2(ar*.8,1.)));
  gl_FragColor=vec4(tonemap(c*v),1.);return;}
 if(uMode==2){ // user photo
  vec2 puv=uv; vec2 sc=uPhotoAR; vec2 ph=(puv-.5)*sc+.5;
  vec3 photo=texture2D(uPhoto,vec2(ph.x,1.-ph.y)).rgb;photo=pow(photo,vec3(2.2));
  vec3 base=photo*(.04+.96*uAmb*uAmb);
  vec2 d=(uv-uProjS)*vec2(ar,1.);float r=length(d);
  vec2 st=d*1.6;
  float spread=smoothstep(1.6,.15,r)*smoothstep(-.12,.08,uv.y-uProjS.y+.05);
  vec3 alb=clamp(photo*1.6+.04,0.,1.);
  vec3 L=effect(st,t)*uInt*spread*1.3;
  vec3 c=base+alb*L;
  float glow=exp(-r*22.)*.9;c+=vec3(.7,.6,1.)*glow*uInt;
  gl_FragColor=vec4(tonemap(c),1.);return;}
 // room mode
 vec2 sp=(uv-.5)*vec2(ar,1.);
 vec3 ro=vec3(sin(uYaw)*.9,1.25,2.75);
 vec3 ta=vec3(sin(uYaw)*-.6,1.85,-3.2);
 vec3 fw=normalize(ta-ro),rt=normalize(cross(fw,vec3(0,1,0))),up=cross(rt,fw);
 vec3 rd=normalize(fw*1.15+rt*sp.x+up*sp.y);
 vec3 RA=vec3(-2.7,0.,-3.2),RB=vec3(2.7,2.75,3.4);
 vec3 inv=1./rd;vec3 t0=(RA-ro)*inv,t1=(RB-ro)*inv;vec3 tf=max(t0,t1);float tR=min(min(tf.x,tf.y),tf.z);
 vec3 nR=-sign(rd)*step(tf.xyz,tf.yzx)*step(tf.xyz,tf.zxy);
 float tHit=tR;vec3 n=nR;vec3 alb;float em=0.;int hitBox=-1;
 for(int i=0;i<8;i++){if(i>=uBn)break;vec3 nb;vec2 tb=boxT(ro,rd,uBmin[i],uBmax[i],nb);if(tb.x<tb.y&&tb.x>0.&&tb.x<tHit){tHit=tb.x;n=nb;hitBox=i;}}
 vec3 x=ro+rd*tHit;
 if(hitBox<0){
  alb=n.y>.5?uFloor:uWall;
  if(n.y<-.5)alb=uWall*1.08;
  // subtle baseboard / ceiling cove
  if(n.y<.5&&n.y>-.5&&x.y<.09)alb*=.6;
  // floor planks
  if(n.y>.5){alb*=.85+.15*noise(vec2(x.x*1.2,floor(x.z*3.)*7.))*1.;}
  // wall art frame on back wall
  if(n.z>.5&&abs(x.x+1.85)<.42&&abs(x.y-1.55)<.3){alb=vec3(.05);if(abs(x.x+1.85)<.38&&abs(x.y-1.55)<.26)alb=vec3(.42,.4,.45);}
  // window on left wall
  if(n.x>.5&&abs(x.z+.6)<.75&&abs(x.y-1.45)<.6){alb=vec3(.02,.03,.06);em=0.;if(abs(x.z+.6)<.71&&abs(x.y-1.45)<.56)alb=vec3(.01,.015,.04)+vec3(.02,.03,.08)*(1.-uAmb);}
 } else {
  for(int i=0;i<8;i++){if(i==hitBox){alb=uBcol[i];em=uBem[i];}}
 }
 // ambient
 float ao=clamp(min(min(x.y,2.75-x.y),min(min(x.x+2.7,2.7-x.x),x.z+3.2))*2.2,0.,1.);
 ao=.55+.45*ao;
 vec3 amb=vec3(1.,.93,.84)*(uAmb*uAmb)*(.55+.45*max(n.y,0.)+.25*max(-n.y,0.))*1.25*ao;
 amb+=vec3(.008,.011,.026)*ao;
 // projection
 vec3 d=x-uP;float dist=length(d);vec3 dir=d/dist;
 vec3 U=normalize(cross(uAxis,vec3(0.,0.,1.)));vec3 V=cross(uAxis,U);
 float ct=dot(dir,uAxis);
 vec3 L=vec3(0.);
 if(ct>.05&&em<.5){
  vec2 st=vec2(dot(dir,U),dot(dir,V))/ct*.62;
  float cone=smoothstep(2.8,1.6,length(st));
  float lam=pow(max(dot(n,-dir),0.),1.4);
  float att=2.4/(.8+dist*dist*.2)*smoothstep(.15,1.,dist);
  float sh=shadow(x+n*.003,uP);
  L=effect(st,t)*cone*lam*att*sh*uInt;
 }
 vec3 c=alb*(amb+L)+alb*em*2.2*uInt;
 if(em>.5){c=mix(alb*.3,effect(vec2(.0,.0),t)+vec3(.4),.5)*1.4*uInt+alb*amb;}
 // projector glow (screen-space halo)
 vec3 pc=uP-ro;float along=dot(pc,rd);vec3 closest=ro+rd*along;float gd=length(closest-uP);
 if(along>0.&&along<tHit+.2)c+=vec3(.75,.65,1.)*exp(-gd*16.)*.5*uInt;
 gl_FragColor=vec4(tonemap(c),1.);
}`;

  const ROOMS = {
    bedroom: { wall: [0.62, 0.6, 0.66], floor: [0.36, 0.28, 0.22], P: [1.4, 0.72, -2.9], axis: [-0.38, 1.0, 0.22], boxes: [
      [[-1.0, 0, -3.2], [1.0, 1.15, -3.08], [0.2, 0.2, 0.26], 0],
      [[-0.95, 0, -3.08], [0.95, 0.38, -1.0], [0.24, 0.22, 0.27], 0],
      [[-0.92, 0.38, -3.05], [0.92, 0.58, -1.02], [0.78, 0.78, 0.82], 0],
      [[-0.8, 0.58, -3.0], [-0.1, 0.72, -2.65], [0.9, 0.9, 0.92], 0],
      [[0.1, 0.58, -3.0], [0.8, 0.72, -2.65], [0.9, 0.9, 0.92], 0],
      [[1.15, 0, -3.15], [1.65, 0.56, -2.7], [0.38, 0.27, 0.19], 0],
      [[1.32, 0.56, -2.98], [1.48, 0.68, -2.82], [0.9, 0.85, 1.0], 1],
      [[-1.7, 0, -1.25], [1.7, 0.012, 0.6], [0.32, 0.3, 0.36], 0]] },
    living: { wall: [0.6, 0.62, 0.64], floor: [0.3, 0.24, 0.2], P: [0.0, 0.52, -1.45], axis: [0.0, 1.0, -0.42], boxes: [
      [[-1.35, 0, -3.2], [1.35, 0.44, -2.3], [0.3, 0.33, 0.4], 0],
      [[-1.35, 0.44, -3.2], [1.35, 0.95, -2.92], [0.3, 0.33, 0.4], 0],
      [[-1.55, 0, -3.2], [-1.35, 0.66, -2.3], [0.28, 0.31, 0.38], 0],
      [[1.35, 0, -3.2], [1.55, 0.66, -2.3], [0.28, 0.31, 0.38], 0],
      [[-0.65, 0, -1.8], [0.65, 0.4, -1.1], [0.42, 0.31, 0.22], 0],
      [[-0.09, 0.4, -1.54], [0.09, 0.52, -1.36], [0.9, 0.85, 1.0], 1],
      [[-1.9, 0, -2.0], [1.9, 0.012, 0.2], [0.36, 0.34, 0.33], 0]] },
    nursery: { wall: [0.68, 0.64, 0.62], floor: [0.42, 0.34, 0.27], P: [1.7, 0.95, -2.95], axis: [-0.5, 1.0, 0.25], boxes: [
      [[-0.75, 0, -3.2], [0.75, 0.95, -2.45], [0.86, 0.84, 0.82], 0],
      [[-0.68, 0.45, -3.13], [0.68, 0.58, -2.52], [0.75, 0.8, 0.92], 0],
      [[1.2, 0, -3.2], [2.2, 0.85, -2.7], [0.82, 0.78, 0.74], 0],
      [[1.62, 0.85, -3.03], [1.78, 0.97, -2.87], [0.9, 0.85, 1.0], 1],
      [[-2.2, 0, -2.0], [-1.5, 0.48, -1.35], [0.55, 0.45, 0.5], 0],
      [[-2.2, 0.48, -2.0], [-2.05, 1.0, -1.35], [0.55, 0.45, 0.5], 0],
      [[-1.5, 0, -1.6], [1.5, 0.012, 0.3], [0.62, 0.55, 0.6], 0]] },
    gaming: { wall: [0.36, 0.38, 0.44], floor: [0.2, 0.2, 0.23], P: [0.85, 0.85, -2.9], axis: [-0.3, 1.0, 0.3], boxes: [
      [[-1.05, 0.72, -3.2], [1.05, 0.77, -2.5], [0.12, 0.12, 0.14], 0],
      [[-1.0, 0, -3.15], [-0.92, 0.72, -2.55], [0.1, 0.1, 0.12], 0],
      [[0.92, 0, -3.15], [1.0, 0.72, -2.55], [0.1, 0.1, 0.12], 0],
      [[-0.6, 0.85, -3.12], [0.6, 1.5, -3.06], [0.08, 0.12, 0.3], 0],
      [[-0.32, 0, -2.25], [0.32, 0.5, -1.7], [0.16, 0.14, 0.2], 0],
      [[-0.32, 0.5, -1.82], [0.32, 1.25, -1.7], [0.18, 0.15, 0.24], 0],
      [[0.77, 0.77, -2.98], [0.93, 0.89, -2.82], [0.9, 0.85, 1.0], 1],
      [[-1.6, 0, -2.4], [1.6, 0.012, -0.6], [0.14, 0.13, 0.17], 0]] }
  };

  class Sky {
    constructor(canvas, opts = {}) {
      this.c = canvas; this.o = Object.assign({ mode: 1, effect: 0, hue: 0, intensity: 1, ambient: 0.15, speed: 1, yaw: 0, room: 'bedroom', scale: 0.85 }, opts);
      const gl = canvas.getContext('webgl', { antialias: false, premultipliedAlpha: false, preserveDrawingBuffer: true }) || canvas.getContext('experimental-webgl');
      if (!gl) { this.failed = true; canvas.closest('[data-cx-sky]')?.classList.add('cx-nogl'); return; }
      this.gl = gl;
      const sh = (type, src) => { const s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s); if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) console.warn(gl.getShaderInfoLog(s)); return s; };
      const pr = gl.createProgram(); gl.attachShader(pr, sh(gl.VERTEX_SHADER, VERT)); gl.attachShader(pr, sh(gl.FRAGMENT_SHADER, FRAG)); gl.linkProgram(pr);
      if (!gl.getProgramParameter(pr, gl.LINK_STATUS)) { this.failed = true; console.warn(gl.getProgramInfoLog(pr)); return; }
      gl.useProgram(pr); this.pr = pr;
      const b = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, b); gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
      const loc = gl.getAttribLocation(pr, 'p'); gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
      this.u = {}; ['uRes', 'uTime', 'uMode', 'uEffect', 'uHue', 'uInt', 'uAmb', 'uYaw', 'uProjS', 'uPhoto', 'uPhotoAR', 'uBn', 'uP', 'uAxis', 'uWall', 'uFloor'].forEach(n => this.u[n] = gl.getUniformLocation(pr, n));
      ['uBmin', 'uBmax', 'uBcol', 'uBem'].forEach(n => this.u[n] = gl.getUniformLocation(pr, n + '[0]'));
      this.tex = gl.createTexture(); gl.bindTexture(gl.TEXTURE_2D, this.tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([20, 20, 30, 255]));
      ['TEXTURE_WRAP_S', 'TEXTURE_WRAP_T'].forEach(k => gl.texParameteri(gl.TEXTURE_2D, gl[k], gl.CLAMP_TO_EDGE));
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      this.photoAR = [1, 1]; this.projS = [0.5, 0.25]; this.t = Math.random() * 100; this.last = performance.now();
      this.setRoom(this.o.room);
      this.resize(); addEventListener('resize', () => this.resize(), { passive: true });
      onVisible(canvas, v => { this.visible = v; if (v) this.loop(); });
    }
    resize() {
      if (this.failed) return;
      const dpr = Math.min(devicePixelRatio || 1, 1.5) * this.o.scale;
      const w = Math.max(2, Math.round(this.c.clientWidth * dpr)), h = Math.max(2, Math.round(this.c.clientHeight * dpr));
      if (this.c.width !== w || this.c.height !== h) { this.c.width = w; this.c.height = h; this.gl.viewport(0, 0, w, h); }
      this.draw();
    }
    setRoom(name) {
      const r = ROOMS[name] || ROOMS.bedroom; this.room = r;
      const flat = (i) => r.boxes.flatMap(b => b[i]);
      const gl = this.gl; if (!gl) return;
      const pad = (arr, n) => { const a = arr.slice(); while (a.length < n) a.push(0); return new Float32Array(a); };
      gl.uniform3fv(this.u.uBmin, pad(flat(0), 24)); gl.uniform3fv(this.u.uBmax, pad(flat(1), 24));
      gl.uniform3fv(this.u.uBcol, pad(flat(2), 24)); gl.uniform1fv(this.u.uBem, pad(r.boxes.map(b => b[3]), 8));
      gl.uniform1i(this.u.uBn, r.boxes.length);
      const ax = r.axis, l = Math.hypot(...ax);
      gl.uniform3fv(this.u.uP, r.P); gl.uniform3fv(this.u.uAxis, ax.map(v => v / l));
      gl.uniform3fv(this.u.uWall, r.wall); gl.uniform3fv(this.u.uFloor, r.floor);
      this.draw();
    }
    setPhoto(img) {
      const gl = this.gl; if (!gl) return;
      const max = 1600, s = Math.min(1, max / Math.max(img.width, img.height));
      const cv = document.createElement('canvas'); cv.width = Math.round(img.width * s); cv.height = Math.round(img.height * s);
      cv.getContext('2d').drawImage(img, 0, 0, cv.width, cv.height);
      gl.bindTexture(gl.TEXTURE_2D, this.tex); gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, cv);
      const car = this.c.clientWidth / this.c.clientHeight, iar = cv.width / cv.height;
      this.photoAR = car > iar ? [1, iar / car] : [car / iar, 1];
      this.o.mode = 2; this.draw();
    }
    set(k, v) { this.o[k] = v; if (k === 'room') this.setRoom(v); this.draw(); }
    draw() {
      const gl = this.gl; if (!gl || this.failed) return; const o = this.o, u = this.u;
      gl.uniform2f(u.uRes, this.c.width, this.c.height); gl.uniform1f(u.uTime, this.t);
      gl.uniform1i(u.uMode, o.mode); gl.uniform1i(u.uEffect, o.effect); gl.uniform1f(u.uHue, o.hue);
      gl.uniform1f(u.uInt, o.intensity); gl.uniform1f(u.uAmb, o.ambient); gl.uniform1f(u.uYaw, o.yaw);
      gl.uniform2fv(u.uProjS, this.projS); gl.uniform2fv(u.uPhotoAR, this.photoAR); gl.uniform1i(u.uPhoto, 0);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }
    loop() {
      if (this.running || this.failed) return; this.running = true;
      const step = (now) => {
        if (!this.visible || document.hidden) { this.running = false; return; }
        const dt = Math.min(0.05, (now - this.last) / 1000); this.last = now;
        if (!reduce) this.t += dt * this.o.speed;
        this.draw(); requestAnimationFrame(step);
      };
      this.last = performance.now(); requestAnimationFrame(step);
    }
  }
  window.CXSky = Sky;
  const EFFECTS = { galaxy: 0, aurora: 1, ocean: 2, laser: 3, planetarium: 4, starmoon: 5 };

  /* ---------------- Effect explorer ---------------- */
  function initExplorer(root) {
    const canvas = $('.cx-fx__canvas', root); const sky = new Sky(canvas, { mode: 1, scale: 0.75 }); root.__sky = sky;
    const tabs = $$('.cx-fx__tab', root), items = $$('.cx-fx__item', root);
    function select(i, focus) {
      tabs.forEach((t, j) => { t.setAttribute('aria-selected', j === i ? 'true' : 'false'); t.tabIndex = j === i ? 0 : -1; });
      items.forEach((it, j) => it.classList.toggle('is-active', j === i));
      const t = tabs[i]; sky.set('effect', EFFECTS[t.dataset.effect] ?? 0); sky.set('hue', parseFloat(t.dataset.hue || 0));
      if (focus) t.focus();
    }
    tabs.forEach((t, i) => {
      t.addEventListener('click', () => { select(i); stopAuto(); });
      t.addEventListener('keydown', e => { if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); const n = (i + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length; select(n, true); stopAuto(); } });
    });
    let auto = null, idx = 0;
    const stopAuto = () => { clearInterval(auto); auto = null; };
    if (!reduce && root.dataset.autoplay === 'true') {
      onVisible(root, v => { if (v && auto === null && !root.dataset.stopped) auto = setInterval(() => { idx = (tabs.findIndex(t => t.getAttribute('aria-selected') === 'true') + 1) % tabs.length; select(idx); }, 6500); else if (!v) stopAuto(); });
      root.addEventListener('pointerdown', () => { root.dataset.stopped = '1'; stopAuto(); });
    }
    select(0);
  }

  /* ---------------- Room visualizer ---------------- */
  function initVisualizer(root) {
    const stage = $('.cx-viz__stage', root); const canvas = $('canvas', stage);
    const data = JSON.parse($('script[type="application/json"]', root)?.textContent || '{}');
    const sky = new Sky(canvas, { mode: 0, effect: 0, ambient: 0.12, intensity: 1.0, scale: 0.8 }); root.__sky = sky;
    if (sky.failed) { stage.insertAdjacentHTML('beforeend', '<p style="position:absolute;inset:0;display:grid;place-items:center;color:#aaa">Your browser does not support the 3D preview.</p>'); return; }
    sky.visible = true; sky.loop();
    const state = { room: 'bedroom', effect: 'galaxy', hue: 0 };
    const press = (group, btn) => { $$('[data-' + group + ']', root).forEach(b => b.setAttribute('aria-pressed', b === btn ? 'true' : 'false')); };
    $$('[data-room]', root).forEach(b => b.addEventListener('click', () => {
      state.room = b.dataset.room; press('room', b); sky.o.mode = 0; sky.set('room', state.room); $('.cx-viz__photo-reset', root)?.setAttribute('hidden', ''); hint('Drag to look around the room');
    }));
    $$('[data-effect]', root).forEach(b => b.addEventListener('click', () => { state.effect = b.dataset.effect; press('effect', b); sky.set('effect', EFFECTS[state.effect]); updateMatch(); }));
    $$('[data-hue]', root).forEach(b => b.addEventListener('click', () => { press('hue', b); sky.set('hue', parseFloat(b.dataset.hue)); }));
    $$('input[type=range]', root).forEach(r => {
      const out = root.querySelector(`label[for="${r.id}"] output`);
      const upd = () => { const v = parseFloat(r.value); r.style.setProperty('--p', ((v - r.min) / (r.max - r.min) * 100) + '%'); if (out) out.textContent = r.dataset.fmt === 'pct' ? Math.round(v * 100) + '%' : (r.dataset.fmt === 'x' ? v.toFixed(1) + '×' : v); sky.set(r.dataset.key, v); };
      r.addEventListener('input', upd); upd();
    });
    // drag: look around (room) or move projector (photo)
    let drag = null;
    stage.addEventListener('pointerdown', e => { if (e.target.closest('button')) return; drag = { x: e.clientX, y: e.clientY, yaw: sky.o.yaw }; stage.setPointerCapture(e.pointerId); hideHint(); });
    stage.addEventListener('pointermove', e => {
      if (!drag) return; const r = stage.getBoundingClientRect();
      if (sky.o.mode === 2) { sky.projS = [(e.clientX - r.left) / r.width, 1 - (e.clientY - r.top) / r.height]; sky.draw(); }
      else { sky.o.yaw = Math.max(-0.55, Math.min(0.55, drag.yaw + (e.clientX - drag.x) / r.width * 1.4)); sky.draw(); }
    });
    const end = () => { drag = null; }; stage.addEventListener('pointerup', end); stage.addEventListener('pointercancel', end);
    // upload
    const file = $('input[type=file]', root);
    file?.addEventListener('change', () => {
      const f = file.files && file.files[0]; if (!f) return;
      const url = URL.createObjectURL(f); const img = new Image();
      img.onload = () => { sky.setPhoto(img); sky.projS = [0.72, 0.32]; sky.set('ambient', parseFloat($('input[data-key=ambient]', root)?.value || 0.12)); $('.cx-viz__photo-reset', root)?.removeAttribute('hidden'); hint('Drag to place your projector'); $$('[data-room]', root).forEach(b => b.setAttribute('aria-pressed', 'false')); URL.revokeObjectURL(url); };
      img.src = url;
    });
    $('.cx-viz__photo-reset', root)?.addEventListener('click', () => { sky.o.mode = 0; sky.set('room', state.room); press('room', $(`[data-room="${state.room}"]`, root)); $('.cx-viz__photo-reset', root).setAttribute('hidden', ''); file.value = ''; });
    $('.cx-viz__fs', root)?.addEventListener('click', () => { const el = stage; (document.fullscreenElement ? document.exitFullscreen() : (el.requestFullscreen || el.webkitRequestFullscreen).call(el))?.catch?.(() => {}); setTimeout(() => sky.resize(), 300); });
    document.addEventListener('fullscreenchange', () => setTimeout(() => sky.resize(), 100));
    $('.cx-viz__snap', root)?.addEventListener('click', () => { sky.draw(); const a = document.createElement('a'); a.download = 'celestial-room-preview.png'; a.href = canvas.toDataURL('image/png'); a.click(); });
    const hintEl = $('.cx-viz__hint', root);
    function hint(t) { if (hintEl) { hintEl.textContent = t; hintEl.style.opacity = 1; } }
    function hideHint() { if (hintEl) hintEl.style.opacity = 0; }
    const match = $('.cx-viz__match', root);
    function updateMatch() {
      const m = data[state.effect]; if (!match || !m) return;
      match.href = m.url; match.querySelector('img').src = m.img; match.querySelector('img').alt = m.title;
      match.querySelector('strong').textContent = m.title; match.querySelector('em').textContent = m.price;
    }
    updateMatch();
  }

  /* ---------------- Product rail ---------------- */
  function initRail(root) {
    const track = $('.cx-rail__track', root), prev = $('[data-prev]', root), next = $('[data-next]', root);
    if (!track) return;
    const upd = () => { if (prev) prev.disabled = track.scrollLeft < 8; if (next) next.disabled = track.scrollLeft + track.clientWidth > track.scrollWidth - 8; };
    const go = (d) => { const card = track.firstElementChild; const w = card ? card.getBoundingClientRect().width + 18 : 300; track.scrollBy({ left: d * w * Math.max(1, Math.floor(track.clientWidth / w) - 1), behavior: 'smooth' }); };
    prev?.addEventListener('click', () => go(-1)); next?.addEventListener('click', () => go(1));
    track.addEventListener('scroll', upd, { passive: true }); addEventListener('resize', upd); upd();
  }

  /* ---------------- Finder quiz ---------------- */
  function initFinder(root) {
    const qs = $$('.cx-finder__q', root), res = $('.cx-finder__result', root), bar = $('.cx-finder__bar i', root);
    const recs = JSON.parse($('script[type="application/json"]', root)?.textContent || '{}');
    const ans = [];
    function show(i) { qs.forEach((q, j) => q.classList.toggle('is-active', j === i)); res.classList.remove('is-active'); if (bar) bar.style.width = ((i + 1) / (qs.length + 1) * 100) + '%'; }
    qs.forEach((q, i) => $$('.cx-finder__opt', q).forEach(o => o.addEventListener('click', () => {
      ans[i] = o.dataset.v;
      if (i < qs.length - 1) show(i + 1); else finish();
    })));
    function finish() {
      // scoring: who (kids/adults/couple/sensory) + vibe (galaxy/aurora/ocean/real) + extra (sound/value/remote)
      const [who, vibe, extra] = ans;
      let key = vibe;
      if (vibe === 'real') key = 'planetarium';
      if (who === 'kids' && vibe === 'galaxy') key = 'astronaut';
      if (who === 'kids' && vibe === 'real') key = 'kids';
      if (who === 'sensory') key = vibe === 'ocean' ? 'ocean' : 'sensory';
      if (extra === 'sound' && (vibe === 'galaxy' || vibe === 'ocean')) key = 'sound';
      if (extra === 'value' && vibe === 'aurora') key = 'aurora_value';
      const r = recs[key] || recs.galaxy || Object.values(recs)[0];
      if (r) {
        const a = $('.cx-finder__pick', res); a.href = r.url; $('img', a).src = r.img; $('img', a).alt = r.title; $('strong', a).textContent = r.title; $('em', a).textContent = r.price; $('.cx-finder__why', res).textContent = r.why || '';
      }
      qs.forEach(q => q.classList.remove('is-active')); res.classList.add('is-active'); if (bar) bar.style.width = '100%';
    }
    $('.cx-finder__restart', root)?.addEventListener('click', () => { ans.length = 0; show(0); });
    show(0);
  }

  /* ---------------- Article enhancements ---------------- */
  function initArticle(root) {
    const prose = $('.cx-prose', root); if (!prose) return;
    // wrap tables
    $$('table', prose).forEach(t => { if (!t.parentElement.classList.contains('cx-table')) { const w = document.createElement('div'); w.className = 'cx-table'; t.parentNode.insertBefore(w, t); w.appendChild(t); } });
    // ensure ids on h2 and build TOC if server did not
    const heads = $$('h2', prose).filter(h => !h.closest('.cp-takeaways'));
    heads.forEach((h, i) => { if (!h.id) h.id = (h.textContent || 'section').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || ('s' + i); });
    const tocs = $$('.cx-toc ol', root);
    tocs.forEach(ol => { if (!ol.children.length) ol.innerHTML = heads.map(h => `<li><a href="#${h.id}">${h.textContent.replace(/</g, '&lt;')}</a></li>`).join(''); });
    const links = $$('.cx-toc a', root);
    // progress + active chapter
    const bar = $('.cx-progress'); const ring = $('.cx-toc__ring .fg', root); const C = ring ? 2 * Math.PI * 14 : 0;
    if (ring) { ring.style.strokeDasharray = C; ring.style.strokeDashoffset = C; }
    const mlabel = $('.cx-mtoc summary span', root);
    let ticking = false;
    function onScroll() {
      ticking = false;
      const r = prose.getBoundingClientRect(); const total = r.height - innerHeight * 0.6;
      const p = Math.max(0, Math.min(1, (-r.top + innerHeight * 0.2) / Math.max(1, total)));
      if (bar) bar.style.transform = `scaleX(${p})`;
      if (ring) ring.style.strokeDashoffset = C * (1 - p);
      let cur = -1; heads.forEach((h, i) => { if (h.getBoundingClientRect().top < innerHeight * 0.3) cur = i; });
      links.forEach(a => a.classList.toggle('is-active', cur >= 0 && a.getAttribute('href') === '#' + heads[cur].id));
      if (mlabel) mlabel.textContent = cur >= 0 ? heads[cur].textContent : 'Overview';
    }
    addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(onScroll); } }, { passive: true }); onScroll();
    links.forEach(a => a.addEventListener('click', () => { const d = a.closest('details'); if (d) d.open = false; }));
    // share: copy link + native share
    $$('[data-cx-copy]', root).forEach(b => b.addEventListener('click', async () => {
      try { if (navigator.share && matchMedia('(pointer:coarse)').matches) { await navigator.share({ title: document.title, url: location.href }); return; } await navigator.clipboard.writeText(location.href); b.setAttribute('data-copied', ''); setTimeout(() => b.removeAttribute('data-copied'), 1600); } catch (e) {}
    }));
    // listen (speech synthesis) — reads the article aloud
    const listen = $('[data-cx-listen]', root);
    if (listen && 'speechSynthesis' in window) {
      listen.hidden = false; let speaking = false;
      listen.addEventListener('click', () => {
        if (speaking) { speechSynthesis.cancel(); speaking = false; listen.querySelector('span').textContent = 'Listen'; return; }
        const txt = [$('.cx-art__title', root)?.textContent, ...$$('h2,h3,p,li', prose).map(e => e.textContent)].join('. ');
        const u = new SpeechSynthesisUtterance(txt.slice(0, 32000)); u.rate = 1.02; u.onend = () => { speaking = false; listen.querySelector('span').textContent = 'Listen'; };
        speechSynthesis.speak(u); speaking = true; listen.querySelector('span').textContent = 'Stop';
      });
    }
  }

  /* ---------------- Blog index search + filter ---------------- */
  function initBlog(root) {
    const input = $('.cx-bsearch input', root), cards = $$('[data-cx-post]', root), empty = $('.cx-empty', root);
    input?.addEventListener('input', () => {
      const q = input.value.trim().toLowerCase(); let n = 0;
      cards.forEach(c => { const hit = !q || c.dataset.cxPost.includes(q); c.style.display = hit ? '' : 'none'; if (hit) n++; });
      if (empty) empty.style.display = n ? 'none' : 'block';
    });
  }

  /* ---------------- Band starfield (lightweight) ---------------- */
  function init() {
    initReveal();
    $$('canvas[data-cx-stars]').forEach(initStarfield);
    $$('[data-cx-explorer]').forEach(initExplorer);
    $$('[data-cx-visualizer]').forEach(initVisualizer);
    $$('[data-cx-rail]').forEach(initRail);
    $$('[data-cx-finder]').forEach(initFinder);
    $$('[data-cx-article]').forEach(initArticle);
    $$('[data-cx-blog]').forEach(initBlog);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', (e) => { initReveal(e.target); $$('canvas[data-cx-stars]', e.target).forEach(initStarfield); $$('[data-cx-explorer]', e.target).forEach(initExplorer); $$('[data-cx-visualizer]', e.target).forEach(initVisualizer); $$('[data-cx-rail]', e.target).forEach(initRail); $$('[data-cx-finder]', e.target).forEach(initFinder); });
})();
